import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RealmHierarchy } from '../../classes/realm-hierarchy';
import { RealmTypePresentation } from '../../enums/realm-type.enum';
import { FilterSelectInputComponent } from '../filter-select-input/filter-select-input.component';

@Component({
  selector: 'app-realm-select-input',
  templateUrl: './realm-select-input.component.html',
  styleUrls: ['./realm-select-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RealmSelectInputComponent,
    },
  ],
})
export class RealmSelectInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  public readonly realmDisplay = (realm: RealmHierarchy) => realm.name;
  public readonly realmValue = (realm: RealmHierarchy) => realm.id;

  @Input()
  public rootRealm: RealmHierarchy;
  public subRealms: RealmHierarchy[];
  public subSubRealms?: RealmHierarchy[];

  public onTouched: Function = () => null;
  private readonly destroyed = new Subject<void>();

  @ViewChild('subRealmInput', { static: true }) public subRealmInput: FilterSelectInputComponent<RealmHierarchy>;
  @ViewChild('subSubRealmInput', { static: true }) public subSubRealmInput: FilterSelectInputComponent<RealmHierarchy>;

  public readonly form = new FormGroup({
    subRealm: new FormControl(),
    subSubRealm: new FormControl(),
  });
  public get subRealm() { return this.form.controls.subRealm; }
  public get subSubRealm() { return this.form.controls.subSubRealm; }

  get value(): string | null {
    return (this.subSubRealm.value || this.subRealm.value) ?? null;
  }

  public get showSubSubRealms(): boolean {
    return !!this.subSubRealms;
  }

  public ngOnInit(): void {
    this.subRealms = this.sorted(this.rootRealm?.subRealms);

    this.subRealm.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((subRealmId: string) => {
        this.subSubRealm.setValue(null);
        const subRealm = RealmHierarchy.findRealm(this.rootRealm, subRealmId);
        if (subRealm) {
          this.subSubRealms = this.sorted(subRealm.subRealms);
        } else {
          this.subSubRealms = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.unsubscribe();
  }

  writeValue(value: any): void {
    if (!value) {
      this.subRealmInput.clear();
      this.subSubRealmInput.clear();
    } else if (typeof value === 'string' || value instanceof String) {
      const realmAncestors = RealmHierarchy.findRealmAncestors(this.rootRealm, value as string);
      if (realmAncestors) {
        if (realmAncestors.length === 2) {
          this.subRealmInput.selectItem(realmAncestors[1]);
        } else if (realmAncestors.length === 3) {
          this.subRealmInput.selectItem(realmAncestors[1]);
          setTimeout(() => this.subSubRealmInput.selectItem(realmAncestors[2]));
        }
      } else {
        throw Error('realm not found');
      }
    } else {
      throw Error('invalid value type');
    }
  }

  public registerOnChange(onChange: Function): void {
    // apparently angular calls registerOnChange after ngOnDestroy has been called for some reason
    // https://stackoverflow.com/questions/69125667/why-is-angular-calling-registeronchange-after-ngondestroy-on-custom-controlv
    if (!this.destroyed.closed) {
      this.form.valueChanges
        .pipe(takeUntil(this.destroyed))
        .subscribe(() => onChange(this.value));
    }
  }

  public registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  public realmTypeLabel(realms: RealmHierarchy[] | undefined): string {
    if (realms?.length > 0) {
      return RealmTypePresentation(realms[0].type);
    } else {
      return '';
    }
  }

  private sorted(realms: RealmHierarchy[]): RealmHierarchy[] {
    return realms?.slice().sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }
}
